import { useState } from 'react';
import { Download, FileText, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { PAPER } from './theme';
import { AuthThumb } from './AuthThumb';

const LIVECOSTS_LABEL = { sent: 'Sent to LiveCosts', partial: 'Partly sent to LiveCosts', failed: 'Not sent to LiveCosts', skipped: 'Not sent to LiveCosts', pending: 'Sending to LiveCosts…' };

/** Phone-friendly viewer for the Cost Inbox invoice behind a ledger row — pages as images + open original. */
export function InvoiceDocViewer({ row, inboxItem, onClose }) {
  const { api } = useAuth();
  const [opening, setOpening] = useState(false);
  const pages = Math.max(1, inboxItem?.page_count || row?.inbox_page_count || 1);

  const openOriginal = async () => {
    setOpening(true);
    try {
      const r = await api.get(`/cost-ledger/entries/${row.id}/document`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const win = window.open(url, '_blank');
      if (!win) {
        const a = document.createElement('a');
        a.href = url; a.download = inboxItem?.original_filename || 'invoice'; a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { toast.error("Couldn't open the invoice file"); }
    finally { setOpening(false); }
  };

  const lc = inboxItem?.livecosts?.status;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden border-0 max-h-[94vh] flex flex-col [&>button.absolute]:hidden" style={{ background: PAPER.page, color: PAPER.ink }} data-testid="invoice-doc-viewer">
        <DialogTitle className="sr-only">Invoice</DialogTitle>
        <DialogDescription className="sr-only">The supplier invoice posted from the Cost Inbox.</DialogDescription>
        <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${PAPER.border}` }}>
          <FileText className="w-4 h-4" style={{ color: PAPER.accent }} />
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-bold truncate" data-testid="invoice-doc-title">{inboxItem?.supplier_name || row.supplier_name || 'Invoice'}{inboxItem?.reference ? ` · ${inboxItem.reference}` : ''}</div>
            <div className="text-[11px] truncate" style={{ color: PAPER.muted }}>{inboxItem?.original_filename}{pages > 1 ? ` · ${pages} pages` : ''}{lc ? ` · ${LIVECOSTS_LABEL[lc] || lc}` : ''}</div>
          </div>
          <button type="button" onClick={openOriginal} disabled={opening} className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg" style={{ background: PAPER.navy, color: PAPER.accent }} data-testid="invoice-doc-open-original">
            {opening ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Original
          </button>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md" style={{ color: PAPER.muted }} data-testid="invoice-doc-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto p-3 space-y-3" style={{ background: PAPER.paperMuted }}>
          {Array.from({ length: pages }, (_, i) => (
            <AuthThumb key={i} endpoint={`/cost-ledger/entries/${row.id}/document/pages/${i}?width=1400`} testKey={`${row.id}-p${i}`} size="lg" fit="contain" className="w-full min-h-[240px] rounded-md" />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
